/**
 * Peer review service — second-reader assignment and assessment.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, apiPut, ApiError, extractApiErrorMessage } from '../utils/apiClient';

export const PEER_REVIEW_REMARKS = [
  'Concur',
  'Minor discrepancy',
  'Clinically significant discrepancy',
] as const;

export type PeerReviewRemark = (typeof PEER_REVIEW_REMARKS)[number];
export type PeerReviewStatus = 'Assigned' | 'Submitted' | 'Expired';

export interface PeerReviewUserSummary {
  id: number;
  full_name?: string;
  email?: string;
  role?: string;
  designation?: string;
  peer_reviewer?: boolean;
}

/** Study fields joined onto peer-review list rows for the Peer Review worklist UI. */
export interface PeerReviewStudySummary {
  id?: number;
  study_instance_uid?: string;
  study_date?: string | null;
  study_time?: string | null;
  modality?: string | null;
  study_description?: string | null;
  body_part?: string | null;
  referring_physician?: string | null;
  clinical_history?: string | null;
  priority?: string | null;
  report_status?: string | null;
  patient_name?: string | null;
  patient_uhid?: string | null;
  patient_age?: string | null;
  patient_gender?: string | null;
  accession_number?: string | null;
  assigned_radiologist_name?: string | null;
  is_critical?: boolean | null;
}

export interface PeerReviewOriginalReportPatient {
  id?: number;
  name?: string | null;
  age?: string | null;
  gender?: string | null;
  patient_dicom_id?: string | null;
  ris_visit_id?: string | null;
}

export interface PeerReviewOriginalReport {
  id?: number;
  study_instance_uid?: string;
  accession_number?: string | null;
  study_date?: string | null;
  study_description?: string | null;
  referred_by?: string | null;
  clinical_notes?: string | null;
  report_status?: string | null;
  patient_id_fk?: number;
  modality_id?: number;
  Patient?: PeerReviewOriginalReportPatient;
  Modality?: { id?: number; name?: string | null };
}

export interface PeerReview {
  id: number;
  study_id_fk?: number;
  study_instance_uid: string;
  report_id_fk?: number;
  original_radiologist_id?: number | null;
  peer_reviewer_id: number;
  assigned_by?: number | null;
  level?: number;
  status: PeerReviewStatus;
  findings?: string | null;
  impression?: string | null;
  remark?: PeerReviewRemark | null;
  comments?: string | null;
  facility_id?: number | null;
  submitted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  is_deleted?: boolean;
  PeerReviewer?: PeerReviewUserSummary;
  OriginalRadiologist?: PeerReviewUserSummary;
  AssignedBy?: PeerReviewUserSummary;
  OriginalReport?: PeerReviewOriginalReport;
  /** Nested study metadata when list API enriches rows. */
  Study?: PeerReviewStudySummary;
  /** Flat study-enriched fields from GET /peer-reviews list. */
  study_date?: string | null;
  study_time?: string | null;
  modality?: string | null;
  study_description?: string | null;
  body_part?: string | null;
  referring_physician?: string | null;
  clinical_history?: string | null;
  priority?: string | null;
  report_status?: string | null;
  patient_name?: string | null;
  patient_uhid?: string | null;
  patient_age?: string | null;
  patient_gender?: string | null;
  accession_number?: string | null;
  /** Peer reviewer display name on the worklist. */
  assigned_radiologist_name?: string | null;
  is_critical?: boolean | null;
}

export interface PeerReviewSummary {
  in_review: number;
  urgent: number;
  routine: number;
}

export interface AssignPeerReviewPayload {
  study_instance_uid?: string;
  study_id?: number;
  peer_reviewer_id: number;
}

export interface SubmitPeerReviewPayload {
  /** Optional — peer review UI uses remark + comments only */
  findings?: string | null;
  /** Optional — peer review UI uses remark + comments only */
  impression?: string | null;
  remark: PeerReviewRemark;
  comments?: string | null;
}

export interface PeerReviewRemarkHistoryItem {
  id: number;
  level?: number;
  status?: PeerReviewStatus;
  remark?: PeerReviewRemark | null;
  comments?: string | null;
  submitted_at?: string | null;
  peer_reviewer_id?: number;
  reviewer_name?: string | null;
  reviewer_designation?: string | null;
  PeerReviewer?: PeerReviewUserSummary;
}

export interface ListPeerReviewsParams {
  /** Single status or comma-separated, e.g. "Assigned,Submitted" */
  status?: PeerReviewStatus | `${PeerReviewStatus},${PeerReviewStatus}` | string;
  study_instance_uid?: string;
  peer_reviewer_id?: number;
  search?: string;
  modality?: string;
  priority?: string;
  needs_action?: 'exhausted';
  workflow_id?: number;
  page?: number;
  limit?: number;
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: PeerReview;
}

export interface PeerReviewListPagination {
  /** Current API shape */
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  /** Legacy aliases */
  currentPage?: number;
}

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: PeerReview[];
  pagination?: PeerReviewListPagination;
  summary?: PeerReviewSummary;
}

interface ApiSummaryResponse {
  success?: boolean;
  message?: string;
  data?: PeerReviewSummary;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return extractApiErrorMessage(error.response, error.message || fallback);
  }
  return error instanceof Error ? error.message : fallback;
}

function firstMeaningful(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) {
      continue;
    }
    if (trimmed.toUpperCase() === 'N/A') {
      continue;
    }
    return trimmed;
  }
  return null;
}

/** Prefer flat list enrichment; fall back to nested `Study` / `OriginalReport` / related users. */
export function getPeerReviewStudyFields(row: PeerReview): PeerReviewStudySummary {
  const nested = row.Study ?? {};
  const report = row.OriginalReport;
  const patient = report?.Patient;

  return {
    id: nested.id,
    study_instance_uid: row.study_instance_uid ?? nested.study_instance_uid,
    study_date: row.study_date ?? nested.study_date ?? report?.study_date ?? null,
    study_time: row.study_time ?? nested.study_time ?? null,
    modality: firstMeaningful(row.modality, nested.modality, report?.Modality?.name) ?? null,
    study_description:
      firstMeaningful(row.study_description, nested.study_description, report?.study_description) ??
      null,
    body_part: firstMeaningful(row.body_part, nested.body_part) ?? null,
    referring_physician:
      firstMeaningful(row.referring_physician, nested.referring_physician, report?.referred_by) ??
      null,
    clinical_history:
      firstMeaningful(row.clinical_history, nested.clinical_history, report?.clinical_notes) ??
      null,
    priority: firstMeaningful(row.priority, nested.priority) ?? 'ROUTINE',
    report_status:
      firstMeaningful(row.report_status, nested.report_status) ??
      (row.status === 'Assigned' ? 'In review' : null),
    patient_name: firstMeaningful(row.patient_name, nested.patient_name, patient?.name) ?? null,
    patient_uhid:
      firstMeaningful(row.patient_uhid, nested.patient_uhid, patient?.patient_dicom_id) ?? null,
    patient_age: firstMeaningful(row.patient_age, nested.patient_age, patient?.age) ?? null,
    patient_gender:
      firstMeaningful(row.patient_gender, nested.patient_gender, patient?.gender) ?? null,
    accession_number:
      firstMeaningful(row.accession_number, nested.accession_number, report?.accession_number) ??
      null,
    assigned_radiologist_name:
      firstMeaningful(
        row.assigned_radiologist_name,
        nested.assigned_radiologist_name,
        row.PeerReviewer?.full_name,
        row.OriginalRadiologist?.full_name
      ) ?? null,
    is_critical: row.is_critical ?? nested.is_critical ?? false,
  };
}

export function getPeerReviewListTotal(
  pagination?: PeerReviewListPagination,
  rowCount = 0
): number {
  if (typeof pagination?.total === 'number') {
    return pagination.total;
  }
  return rowCount;
}

export async function assignPeerReview(payload: AssignPeerReviewPayload): Promise<PeerReview> {
  try {
    const response = await apiPost<ApiSingleResponse>(API_ENDPOINTS.PEER_REVIEWS.ASSIGN, payload);
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Failed to assign peer review');
    }
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to assign peer review'));
  }
}

export async function listPeerReviews(params?: ListPeerReviewsParams): Promise<{
  peerReviews: PeerReview[];
  pagination?: ApiListResponse['pagination'];
  summary?: PeerReviewSummary;
}> {
  try {
    const response = await apiGet<ApiListResponse>(API_ENDPOINTS.PEER_REVIEWS.LIST(params));
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to fetch peer reviews');
    }
    return {
      peerReviews: Array.isArray(response.data.data) ? response.data.data : [],
      pagination: response.data.pagination,
      summary: response.data.summary,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer reviews'));
  }
}

export async function getPeerReviewSummary(): Promise<PeerReviewSummary> {
  try {
    const response = await apiGet<ApiSummaryResponse>(API_ENDPOINTS.PEER_REVIEWS.SUMMARY);
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Failed to fetch peer review summary');
    }
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer review summary'));
  }
}

export async function getPeerReviewById(id: string | number): Promise<PeerReview> {
  try {
    const response = await apiGet<ApiSingleResponse>(API_ENDPOINTS.PEER_REVIEWS.GET_BY_ID(id));
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Failed to fetch peer review');
    }
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer review'));
  }
}

export async function getPeerReviewByStudyUid(
  studyInstanceUid: string
): Promise<PeerReview | null> {
  try {
    const response = await apiGet<ApiSingleResponse>(
      API_ENDPOINTS.PEER_REVIEWS.GET_BY_STUDY_UID(studyInstanceUid)
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to fetch peer review');
    }
    return response.data.data ?? null;
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      return null;
    }
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer review'));
  }
}

/** Previous reviewer remarks (Submitted levels) for Peer Review Comments UI. */
export async function getPeerReviewRemarksHistory(
  studyInstanceUid: string
): Promise<PeerReviewRemarkHistoryItem[]> {
  try {
    const response = await apiGet<{
      success?: boolean;
      message?: string;
      data?: PeerReviewRemarkHistoryItem[];
    }>(API_ENDPOINTS.PEER_REVIEWS.HISTORY_BY_STUDY_UID(studyInstanceUid));
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to fetch peer review history');
    }
    return Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer review history'));
  }
}

export async function submitPeerReview(
  id: string | number,
  payload: SubmitPeerReviewPayload
): Promise<PeerReview> {
  try {
    const response = await apiPut<ApiSingleResponse>(
      API_ENDPOINTS.PEER_REVIEWS.SUBMIT(id),
      payload
    );
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'Failed to submit peer assessment');
    }
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to submit peer assessment'));
  }
}

export async function comparePeerReview(id: string | number): Promise<unknown> {
  try {
    const response = await apiGet<{ success?: boolean; message?: string; data?: unknown }>(
      API_ENDPOINTS.PEER_REVIEWS.COMPARE(id)
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to fetch peer review comparison');
    }
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to fetch peer review comparison'));
  }
}
